<script setup lang="ts">
/**
 * Forum-Channel nach aktuellem Client (aus echten Screenshots abgenommen):
 * Header (Forum-Icon + Name, rechts Bell/Members), darunter die große
 * Suchleiste mit Inbox-Button und dem Blurple-„New Post"-Button, dann die
 * Sortier-/Tag-Filterzeile, dann die Post-Liste (volle Zeilen, Slot).
 */
withDefaults(defineProps<{
  channel: string
  tags?: { label: string, emoji?: string, active?: boolean }[]
  searchPlaceholder?: string
}>(), {
  tags: () => [],
  searchPlaceholder: 'Search or create a post…'
})
</script>

<template>
  <div class="dc-forum">
    <div class="dc-forum-header">
      <svg class="dc-forum-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 3h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-4.6L12 20.4 9.6 17H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />
        <path d="M8 8.2h8M8 12h5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      </svg>
      <span class="dc-forum-name">{{ channel }}</span>
      <div class="dc-forum-header-icons" aria-hidden="true">
        <UIcon name="i-lucide-bell" class="size-[18px]" />
        <UIcon name="i-lucide-users" class="size-[18px]" />
      </div>
    </div>

    <div class="dc-forum-toolbar" aria-hidden="true">
      <div class="dc-forum-search">
        <UIcon name="i-lucide-search" class="size-[16px]" />
        <span>{{ searchPlaceholder }}</span>
      </div>
      <span class="dc-forum-inbox">
        <UIcon name="i-lucide-inbox" class="size-[16px]" />
      </span>
      <span class="dc-forum-newpost">
        <UIcon name="i-lucide-message-circle" class="size-[15px]" />
        New Post
      </span>
    </div>

    <div v-if="tags.length" class="dc-forum-tagbar" aria-hidden="true">
      <span class="dc-forum-sort">
        <UIcon name="i-lucide-arrow-up-down" class="size-[14px]" />
        Sort &amp; view
        <UIcon name="i-lucide-chevron-down" class="size-[13px]" />
      </span>
      <span class="dc-forum-tagbar-divider" />
      <DiscordTag v-for="t in tags" :key="t.label" :label="t.label" :emoji="t.emoji" :active="t.active" />
      <span class="dc-forum-all">
        All
        <UIcon name="i-lucide-chevron-down" class="size-[13px]" />
      </span>
    </div>

    <div class="dc-forum-grid">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.dc-forum {
  display: flex;
  flex-direction: column;
  background: var(--dc-bg-chat);
  border: 1px solid var(--dc-hairline);
  border-radius: var(--radius-md);
  overflow: hidden;
  font-family: var(--font-discord);
  box-shadow: var(--shadow-float);
}

.dc-forum-header {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 16px;
  border-bottom: 1px solid var(--dc-hairline);
  flex-shrink: 0;
}

.dc-forum-icon {
  color: var(--dc-ink-channel);
  flex-shrink: 0;
}

.dc-forum-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--dc-ink-header);
}

.dc-forum-header-icons {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--dc-ink-channel);
}

/* Große Such-/Aktionszeile */
.dc-forum-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 8px;
  flex-shrink: 0;
}

.dc-forum-search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 9px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  color: var(--dc-ink-channel);
  font-size: 0.9rem;
  border-radius: 10px;
  padding: 8px 12px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
}

.dc-forum-inbox {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  color: var(--dc-ink-channel);
  border-radius: 10px;
  flex-shrink: 0;
}

.dc-forum-newpost {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--dc-blurple);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 10px;
  padding: 8px 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Sortier-/Tag-Zeile */
.dc-forum-tagbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 14px 10px;
  flex-shrink: 0;
}

.dc-forum-sort {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--dc-ink);
  white-space: nowrap;
}

.dc-forum-tagbar-divider {
  width: 1px;
  height: 20px;
  background: var(--dc-border-card);
  margin: 0 4px;
}

.dc-forum-all {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--dc-ink);
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: var(--radius-pill);
  padding: 4px 12px;
}

.dc-forum-grid {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 14px 14px;
  overflow: hidden;
}
</style>
